import SelectFormField from '@components/Common/SelectFormField';
import { MANAGED, MANAGED_WITHOUT_RECRUITER_WARNING } from '@components/Common/Utils/hiringMode';
import {
  assignJobHrMutation,
  countUnownedCompanyJobs,
  employerJobsForAssignment,
  setJobHiringModeMutation,
} from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { Box, Typography } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './JobAssignmentList.scss';

interface AssignJob {
  _id: string;
  title?: string;
  jobType?: string;
  hiringMode?: string;
  jobStatus?: string;
  assignedHrId?: string | null;
}

interface HrUser {
  _id: string;
  name: string;
}

/**
 * Per-job HR assignment for one managed client: lists the jobs that account holds, each with an
 * "Assigned HR" dropdown (one HR per job) firing `assignJobHr`. Self-loads its jobs + HR users.
 * Central home is the /hr page; the employer workspace shows a read-only summary that links here.
 *
 * **Scoped to the employer account, not to its company.** It read the whole company for one
 * release, to answer a client showing 2 of a page of live listings. That over-collects: a jewellery
 * group runs several franchises, each its own employer account on its own plan, and only one may
 * subscribe to Managed hiring. The company view put a sibling franchise's roles, and an LTD
 * account's paid-for listings, one click away from being handed to a Mintly recruiter.
 *
 * The short list has a different cause and its own screen. Ownership is `Job.employerId`, Mintly
 * posts on a client's behalf long before they sign up, and 7,007 of 7,197 production jobs carry no
 * owner at all. Those are claimed for an account in the employer workspace's Jobs tab, and appear
 * here once claimed. `unownedCount` says so on screen, because the first time this list looked
 * short nothing explained why and the answer was assumed to be a bug in the query.
 *
 * **It lists every job the account holds, not only the MANAGED ones.** It used to filter to
 * `hiringMode === MANAGED` client-side and say "No managed jobs yet" otherwise, which read as "this
 * client has posted nothing" when the truth was "nothing here has been marked managed" -- and the
 * two want opposite actions.
 */
const JobAssignmentList: React.FC<{ employerId: string; companyId?: string | null }> = ({
  employerId,
  companyId,
}) => {
  const [jobs, setJobs] = useState<AssignJob[]>([]);
  const [hrUsers, setHrUsers] = useState<HrUser[]>([]);
  const [loading, setLoading] = useState(true);
  // The row currently being written, so a second change cannot race the first.
  const [pendingId, setPendingId] = useState<string | null>(null);
  // How many jobs on this client's company nobody holds. The reason this list can look short.
  const [unownedCount, setUnownedCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [jr, ur, cr] = await Promise.all([
        fetchGraphQLApiData(employerJobsForAssignment(employerId)).catch(() => null),
        axios.get('/api/users?page=0&size=200').catch(() => null),
        companyId
          ? fetchGraphQLApiData(countUnownedCompanyJobs(companyId)).catch(() => null)
          : Promise.resolve(null),
      ]);
      setJobs(jr?.data?.getAllJobs?.content ?? []);
      setUnownedCount(cr?.data?.countUnownedCompanyJobs ?? 0);
      const list = ur?.data?.content ?? ur?.data ?? [];
      setHrUsers(
        list
          .filter((u: any) => u?.role === 'HR')
          .map((u: any) => ({
            _id: u._id,
            name:
              u?.personalDetails?.fullName ||
              [u?.personalDetails?.firstName, u?.personalDetails?.lastName].filter(Boolean).join(' ') ||
              u._id,
          })),
      );
    } finally {
      setLoading(false);
    }
  }, [companyId, employerId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Give a job an HR, and make it MANAGED in the same action if it was not already.
   *
   * Naming a Mintly recruiter for a role *is* the managed decision, and splitting it into two
   * clicks would leave a job MANAGED with nobody on it in between -- the one state that hides its
   * applicants from every HR (the access rule is `hiringMode != MANAGED OR assignedHrId == me`).
   *
   * Order matters and is the safe way round. `assignJobHr` writes the id without reading the mode,
   * so a job that ends up with an assignedHrId and no MANAGED is inert: the id alone gates nothing.
   * The reverse -- MANAGED written first, then a failed assign -- is the applicant-hiding state,
   * reached by a half-finished action rather than by a decision.
   */
  const assign = async (jobId: string, hrUserId: string) => {
    const job = jobs.find((j) => j._id === jobId);
    setPendingId(jobId);
    try {
      await fetchGraphQLApiData(assignJobHrMutation(jobId, hrUserId || null), {
        showStatus: true,
        message: 'Job assignment updated',
      });
      const nowManaged = !!hrUserId && job?.hiringMode !== MANAGED;
      if (nowManaged) {
        await fetchGraphQLApiData(setJobHiringModeMutation(jobId, MANAGED), {
          showStatus: true,
          message: 'Job moved to Premium Hiring',
        });
      }
      setJobs((js) =>
        js.map((j) =>
          j._id === jobId
            ? {
                ...j,
                assignedHrId: hrUserId || null,
                hiringMode: nowManaged ? MANAGED : j.hiringMode,
              }
            : j,
        ),
      );
    } finally {
      setPendingId(null);
    }
  };

  // Managed roles first: they are the live book, and the rest are candidates to bring into it.
  // Stable within each group, so the query's newest-first order survives.
  const ordered = [
    ...jobs.filter((j) => j.hiringMode === MANAGED),
    ...jobs.filter((j) => j.hiringMode !== MANAGED),
  ];

  /*
   * Why the list may look shorter than the client's real book, and the way out of it.
   *
   * Unowned jobs sit on the company with no account behind them, so they cannot appear on an
   * account's list until somebody claims them. Saying so is not enough on its own: the count is a
   * number an operator can do nothing with from here, and the screen that fixes it is two
   * navigations away and not obviously related. The link goes straight to the picker, which is
   * where ownership is decided -- deliberately a different screen, because claiming a job moves
   * its applicant list and edit rights with it and is Super-Admin only.
   */
  const unownedNote = unownedCount > 0 && (
    <Typography className="ja-note ja-note--hint">
      {unownedCount} job{unownedCount === 1 ? '' : 's'} on this client&apos;s company{' '}
      {unownedCount === 1 ? 'has' : 'have'} no owner, so {unownedCount === 1 ? 'it' : 'they'} cannot
      be assigned here.{' '}
      <Link className="ja-link" to={`/employers/${employerId}/jobs`}>
        Associate {unownedCount === 1 ? 'it' : 'them'} with this employer
      </Link>{' '}
      to bring {unownedCount === 1 ? 'it' : 'them'} into this list.
    </Typography>
  );

  if (loading) return <Typography className="ja-note">Loading jobs…</Typography>;
  if (ordered.length === 0) {
    return (
      <>
        <Typography className="ja-note">
          This account holds no jobs yet. Jobs posted by this employer appear here, as do any
          claimed for them from their company.
        </Typography>
        {unownedNote}
      </>
    );
  }

  return (
    <>
      <Typography className="ja-caption">
        Jobs held by this account. Assigning an HR also moves that job to Premium Hiring.
      </Typography>
      <Box className="ja-list">
        {ordered.map((j) => {
          const managedWithoutHr = j.hiringMode === MANAGED && !j.assignedHrId;
          return (
            <Box key={j._id} className="ja-row">
              <Box className="ja-job">
                <Typography className="ja-title">{j.title || 'Untitled'}</Typography>
                <Typography className="ja-meta">
                  {[j.jobType, j.jobStatus, j.hiringMode === MANAGED ? 'Premium Hiring' : 'Self-serve']
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
                {managedWithoutHr && (
                  <Typography className="ja-warn">{MANAGED_WITHOUT_RECRUITER_WARNING}</Typography>
                )}
              </Box>
              <Box className="ja-select">
                <SelectFormField
                  required={false}
                  disabled={pendingId === j._id}
                  name={`assignedHr-${j._id}`}
                  label=""
                  size="small"
                  value={j.assignedHrId ?? ''}
                  options={[
                    { value: '', label: 'Unassigned' },
                    ...hrUsers.map((u) => ({ value: u._id, label: u.name })),
                  ]}
                  onChange={(_name, value) => assign(j._id, String(value))}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
      {unownedNote}
    </>
  );
};

export default JobAssignmentList;
